import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Card, Empty, Segmented, Spin, Tag, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";
import isoWeek from "dayjs/plugin/isoWeek";
import { formatMoney } from "../../../utils/formatUtils";
import DateRangeSelectorCard, { resolveDateRange } from "../../../components/DateRangeSelectorCard";
import {
  getInstitution,
  getInstitutionIncomeSummary,
  getInstitutionExpenseSummary,
} from "../../../api/institutionApi";
import { getTypeColor } from "../../../utils/themeUtils";
import StatementCompareTable from "../../../components/statements/StatementCompareTable";
import { createStatementCompareColumns } from "../../../components/statements/StatementCompareColumns";

dayjs.extend(isoWeek);
dayjs.extend(updateLocale);
dayjs.locale("mn");
dayjs.updateLocale("mn", { weekStart: 1 });

const { Title } = Typography;

function SummaryCard({ title, amount, currency, type = "income" }) {
  const isIncome = type === "income";

  const accent = isIncome
    ? {
        card: "border-neutral-200 bg-white text-neutral-900",
        title: "text-emerald-700",
        pill: "border-emerald-200 bg-emerald-50 text-emerald-700",
        bar: "bg-emerald-500",
      }
    : {
        card: "border-neutral-200 bg-white text-neutral-900",
        title: "text-rose-700",
        pill: "border-rose-200 bg-rose-50 text-rose-700",
        bar: "bg-rose-500",
      };

  return (
    <Card
      className={`overflow-hidden rounded-2xl border ${accent.card}`}
      styles={{ body: { padding: 0 } }}
    >
      <div className={`h-1 w-full ${accent.bar}`} />
      <div className="flex flex-col gap-5 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className={`text-xs font-medium uppercase tracking-[0.12em] ${accent.title}`}>
            {title}
          </div>
          <div className={`rounded-full border px-3 py-1 text-[11px] font-semibold tracking-[0.12em] ${accent.pill}`}>
            {currency || "-"}
          </div>
        </div>
        <div className="break-all text-[24px] font-semibold leading-none text-neutral-900">
          {formatMoney(amount ?? 0, currency)}
        </div>
      </div>
    </Card>
  );
}

function TableBottomSummary({ label, amount, currency, amountClassName = "" }) {
  return (
    <div className="flex items-center justify-between rounded-b-2xl border-x border-b bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className={`text-sm font-semibold text-slate-900 ${amountClassName}`}>
        {formatMoney(amount ?? 0, currency)}
      </span>
    </div>
  );
}

function getCategoryColor(categoryKey) {
  const colorMap = {
    MAIN_NON_BANK: "blue",
    OTHER_INSTITUTIONS: "purple",
    RETURNED: "cyan",
    OTHER: "default",
    TO_MAIN_INSTITUTION: "blue",
    TO_ANOTHER_INSTITUTION: "gold",
    INSIDE_INSTITUTION: "green",
    BY_ALLOCATION_EXPENSE: "purple",
    TOTAL_FEE: "red",
  };
  return colorMap[categoryKey] || "default";
}

function normalizeCategoryLabel(label) {
  const map = {
    "Main non-bank income": "Банк бусаас",
    "Other institutions": "Бусад трейдинг хаусаас",
    Returned: "Буцаалт",
    Other: "Бусад",
    "To main institution": "Банк бус руу",
    "To another institution": "Бусад трейдинг хаус руу",
    "Inside institution": "Байгууллага дотор",
    "By allocation expense": "Хуваарьт гүйлгээ",
    "Total fee": "Нийт шимтгэл",
  };
  return map[label] || label || "-";
}

function splitStatementsBySource(category) {
  const statements = Array.isArray(category?.statements) ? category.statements : [];

  const bankStatements = statements.filter((item) => item?.sourceType === "BANK_STATEMENT");
  const bankReportStatements = statements.filter((item) => item?.sourceType === "BANK_REPORT_STATEMENT");

  const originalTotalAmount = bankStatements.reduce((sum, s) => {
    const income = Number(s?.income || 0);
    const expense = Number(s?.expense || 0);
    return sum + (income > 0 ? income : expense);
  }, 0);

  const reportTotalAmount = bankReportStatements.reduce((sum, s) => {
    const income = Number(s?.income || 0);
    const expense = Number(s?.expense || 0);
    return sum + (income > 0 ? income : expense);
  }, 0);

  return {
    bankStatements,
    bankReportStatements,
    originalTotalAmount,
    reportTotalAmount,
    difference: originalTotalAmount - reportTotalAmount,
  };
}

export default function InstitutionAnalyticsPage() {
  const { institutionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedCurrency = searchParams.get("currency") || null;

  const [institution, setInstitution] = useState(null);
  const [institutionLoading, setInstitutionLoading] = useState(true);
  const [institutionError, setInstitutionError] = useState("");

  const [rangeMode, setRangeMode] = useState("week");
  const [selectedWeek, setSelectedWeek] = useState(() => dayjs());
  const [customRange, setCustomRange] = useState(() => [
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);

  const [activeFlow, setActiveFlow] = useState("income");
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("ALL");

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [incomeData, setIncomeData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);

  useEffect(() => {
    if (!institutionId) return;

    async function fetchInstitution() {
      try {
        setInstitutionLoading(true);
        setInstitutionError("");
        const data = await getInstitution(institutionId);
        setInstitution(data || null);
      } catch (err) {
        setInstitutionError(err?.message || "Байгууллагын мэдээллийг ачаалж чадсангүй");
        setInstitution(null);
      } finally {
        setInstitutionLoading(false);
      }
    }

    fetchInstitution();
  }, [institutionId]);

  const resolvedDateRange = useMemo(() => {
    return resolveDateRange(rangeMode, selectedWeek, customRange);
  }, [rangeMode, selectedWeek, customRange]);

  const startDate = resolvedDateRange.startDate;
  const endDate = resolvedDateRange.endDate;

  useEffect(() => {
    if (!institutionId || !selectedCurrency || !resolvedDateRange?.isValid || !startDate || !endDate) {
      setIncomeData(null);
      setExpenseData(null);
      setSummaryError("");
      return;
    }

    async function loadSummaries() {
      try {
        setSummaryLoading(true);
        setSummaryError("");

        const [incomeResponse, expenseResponse] = await Promise.all([
          getInstitutionIncomeSummary(institutionId, startDate, endDate),
          getInstitutionExpenseSummary(institutionId, startDate, endDate),
        ]);

        setIncomeData(incomeResponse || null);
        setExpenseData(expenseResponse || null);
      } catch (err) {
        setSummaryError(err?.message || "Тайлант гүйлгээний мэдээлэл ачааллахад алдаа гарлаа");
        setIncomeData(null);
        setExpenseData(null);
      } finally {
        setSummaryLoading(false);
      }
    }

    loadSummaries();
  }, [institutionId, selectedCurrency, resolvedDateRange, startDate, endDate]);

  const mergedCurrencySummaries = useMemo(() => {
    if (!selectedCurrency) return [];

    const incomeGroup =
      (incomeData?.currencyGroups || []).find((g) => g?.currency === selectedCurrency) || {
        currency: selectedCurrency,
        totalAmount: 0,
        categories: [],
      };

    const expenseGroup =
      (expenseData?.currencyGroups || []).find((g) => g?.currency === selectedCurrency) || {
        currency: selectedCurrency,
        totalAmount: 0,
        categories: [],
      };

    return [{ currency: selectedCurrency, income: incomeGroup, expense: expenseGroup }];
  }, [selectedCurrency, incomeData, expenseData]);

  const selectedCurrencyGroup = mergedCurrencySummaries[0] || null;

  const activeSummary = useMemo(() => {
    if (!selectedCurrencyGroup) return null;
    return activeFlow === "income" ? selectedCurrencyGroup.income : selectedCurrencyGroup.expense;
  }, [selectedCurrencyGroup, activeFlow]);

  const activeCategories = useMemo(() => {
    return Array.isArray(activeSummary?.categories) ? activeSummary.categories : [];
  }, [activeSummary]);

  useEffect(() => {
    if (!activeCategories.length) {
      setSelectedCategoryKey("ALL");
      return;
    }

    if (
      selectedCategoryKey !== "ALL" &&
      !activeCategories.some((c) => c.categoryKey === selectedCategoryKey)
    ) {
      setSelectedCategoryKey("ALL");
    }
  }, [activeCategories, selectedCategoryKey]);

  const selectedCategory = useMemo(() => {
    if (selectedCategoryKey === "ALL") {
      return {
        categoryKey: "ALL",
        categoryLabel: "Бүгд",
        totalAmount: activeCategories.reduce((sum, c) => sum + Number(c?.totalAmount || 0), 0),
        statements: activeCategories.flatMap((c) => (Array.isArray(c?.statements) ? c.statements : [])),
      };
    }

    return activeCategories.find((c) => c.categoryKey === selectedCategoryKey) || null;
  }, [activeCategories, selectedCategoryKey]);

  const categorySplit = useMemo(() => splitStatementsBySource(selectedCategory), [selectedCategory]);

  const allCategorySplit = useMemo(() => {
    return splitStatementsBySource({
      statements: activeCategories.flatMap((c) => (Array.isArray(c?.statements) ? c.statements : [])),
    });
  }, [activeCategories]);

  const statementColumns = createStatementCompareColumns(
    selectedCurrencyGroup?.currency || selectedCurrency || ""
  );

  if (institutionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Spin size="large" />
      </div>
    );
  }

  if (institutionError) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-8">
        <Alert type="error" message={institutionError} showIcon className="rounded-2xl" />
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Empty description="Байгууллага олдсонгүй" />
      </div>
    );
  }

  const institutionName = institution.name || "Байгууллага";

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto max-w-screen-2xl space-y-5">

        <div className="flex items-center gap-3">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() =>
              navigate(
                `/institutions/${institutionId}/wallets${selectedCurrency ? `?currency=${selectedCurrency}` : ""}`
              )
            }
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Title level={4} className="!mb-0 truncate text-slate-900">
                {institutionName}
              </Title>
              <Tag color={getTypeColor(institution?.type || "UNKNOWN")} className="shrink-0">
                {institution?.type || "UNKNOWN"}
              </Tag>
              {selectedCurrency ? (
                <Tag color="blue" className="shrink-0 !px-3 !py-1 !font-semibold">
                  {selectedCurrency}
                </Tag>
              ) : null}
            </div>
          </div>
        </div>

        <DateRangeSelectorCard
          rangeMode={rangeMode}
          onRangeModeChange={setRangeMode}
          selectedWeek={selectedWeek}
          onSelectedWeekChange={setSelectedWeek}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />

        {!selectedCurrency ? (
          <Card className="rounded-2xl">
            <div className="p-8">
              <Empty description="Валют сонгогдоогүй байна" />
            </div>
          </Card>
        ) : summaryError ? (
          <Alert type="error" message="Ачааллах алдаа" description={summaryError} showIcon />
        ) : !selectedCurrencyGroup ? (
          <Card className="rounded-2xl">
            <div className="p-8">
              <Empty description="Валютын мэдээлэл алга" />
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SummaryCard
                title="Нийт орлого"
                amount={selectedCurrencyGroup?.income?.totalAmount || 0}
                currency={selectedCurrencyGroup?.currency}
                type="income"
              />
              <SummaryCard
                title="Нийт зарлага"
                amount={selectedCurrencyGroup?.expense?.totalAmount || 0}
                currency={selectedCurrencyGroup?.currency}
                type="expense"
              />
            </div>

            <Card
              className="rounded-[24px] border border-slate-200 shadow-sm"
              styles={{ body: { padding: 18 } }}
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-lg font-semibold text-slate-900">Мөнгөн урсгал</div>
                  <Tag color="blue" className="!rounded-full !px-3 !py-1 !font-bold">
                    {selectedCurrencyGroup.currency}
                  </Tag>
                </div>
                <Segmented
                  value={activeFlow}
                  onChange={setActiveFlow}
                  size="large"
                  className="rounded-2xl bg-slate-100 p-1"
                  options={[
                    { label: "Орлого", value: "income" },
                    { label: "Зарлага", value: "expense" },
                  ]}
                />
              </div>
            </Card>

            <Card className="rounded-2xl border-0 shadow-sm">
              {activeCategories.length === 0 ? (
                <Empty description="Ангиллын мэдээлэл алга" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button
                    key="ALL"
                    type={selectedCategoryKey === "ALL" ? "primary" : "default"}
                    onClick={() => setSelectedCategoryKey("ALL")}
                    className="!h-auto !rounded-full !px-4 !py-2"
                  >
                    <div className="flex flex-col items-start leading-tight text-left">
                      <span className="font-medium">Бүгд</span>
                      <span className="text-xs opacity-80">
                        Ориг: {formatMoney(allCategorySplit.originalTotalAmount, selectedCurrencyGroup.currency)}
                      </span>
                      <span className="text-xs opacity-80">
                        Хийсвэр: {formatMoney(allCategorySplit.reportTotalAmount, selectedCurrencyGroup.currency)}
                      </span>
                    </div>
                  </Button>

                  {activeCategories.map((category) => {
                    const active = selectedCategoryKey === category.categoryKey;
                    const split = splitStatementsBySource(category);

                    return (
                      <Button
                        key={category.categoryKey}
                        type={active ? "primary" : "default"}
                        onClick={() => setSelectedCategoryKey(category.categoryKey)}
                        className="!h-auto !rounded-full !px-4 !py-2"
                      >
                        <div className="flex flex-col items-start leading-tight text-left">
                          <span className="font-medium">
                            {normalizeCategoryLabel(category.categoryLabel)}
                          </span>
                          <span className="text-xs opacity-80">
                            Ориг: {formatMoney(split.originalTotalAmount, selectedCurrencyGroup.currency)}
                          </span>
                          <span className="text-xs opacity-80">
                            Хийсвэр: {formatMoney(split.reportTotalAmount, selectedCurrencyGroup.currency)}
                          </span>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              )}
            </Card>

            {selectedCategory ? (
              <div className="rounded-2xl border bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Tag color={getCategoryColor(selectedCategory.categoryKey)}>
                    {normalizeCategoryLabel(selectedCategory.categoryLabel)}
                  </Tag>
                  <Tag>
                    Нийт: {formatMoney(selectedCategory.totalAmount || 0, selectedCurrencyGroup.currency)}
                  </Tag>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <div>
                    <StatementCompareTable
                      title={`${selectedCurrencyGroup.currency} Ориг (${categorySplit.bankStatements.length})`}
                      data={categorySplit.bankStatements}
                      columns={statementColumns}
                      loading={summaryLoading}
                    />
                    <TableBottomSummary
                      label="Нийт (ориг)"
                      amount={categorySplit.originalTotalAmount}
                      currency={selectedCurrencyGroup.currency}
                    />
                  </div>

                  <div>
                    <StatementCompareTable
                      title={`${selectedCurrencyGroup.currency} Хийсвэр (${categorySplit.bankReportStatements.length})`}
                      data={categorySplit.bankReportStatements}
                      columns={statementColumns}
                      loading={summaryLoading}
                    />
                    <TableBottomSummary
                      label="Нийт (хийсвэр)"
                      amount={categorySplit.reportTotalAmount}
                      currency={selectedCurrencyGroup.currency}
                    />
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border bg-slate-50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">
                      Зөрүү (Ориг - Хийсвэр)
                    </span>
                    <span
                      className={`text-base font-semibold ${
                        categorySplit.difference === 0
                          ? "text-slate-900"
                          : categorySplit.difference > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {formatMoney(categorySplit.difference, selectedCurrencyGroup.currency)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <Card className="rounded-2xl">
                <div className="p-8">
                  <Empty description="Ангилал сонгоно уу" />
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
